import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { createPhaseSchema, type CreatePhaseInput } from "@aos/shared";
import { Label } from "@/components/ui/label";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";
import { FormShell } from "../skills/_components/FormShell";
import {
  IntCheckboxField,
  NumberField,
  TextAreaField,
  TextField,
} from "../skills/_components/form";

export interface PhaseFormProps {
  action: "create" | "edit";
}

/** Create/edit form for a Phase. Validated against `createPhaseSchema`. */
export function PhaseForm({ action }: PhaseFormProps) {
  const form = useForm<CreatePhaseInput>({
    resolver: zodResolver(createPhaseSchema),
    refineCoreProps: { resource: "phases", action },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
    watch,
    setValue,
  } = form;

  const context = watch("context_markdown") ?? "";

  return (
    <FormShell
      title={action === "create" ? "New Phase" : "Edit Phase"}
      subtitle="Place this step in a process, with order and start/terminal markers."
      resource="phases"
      isSubmitting={formLoading}
      onSubmit={handleSubmit((values) => onFinish(values))}
    >
      <TextField
        form={form}
        name="process_id"
        label="Process ID"
        placeholder="prc_…"
        description="The process this phase belongs to."
      />
      <TextField form={form} name="name" label="Name" placeholder="Phase name" />
      <NumberField
        form={form}
        name="sort_order"
        label="Sort Order"
        placeholder="0"
      />
      <TextAreaField
        form={form}
        name="purpose"
        label="Purpose"
        placeholder="What happens in this phase"
      />
      <div className="space-y-1.5">
        <Label htmlFor="context_markdown">Context (Markdown)</Label>
        <MarkdownEditor
          value={context}
          onChange={(next) =>
            setValue("context_markdown", next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      </div>
      <div className="flex gap-8">
        <IntCheckboxField form={form} name="is_start" label="Start phase" />
        <IntCheckboxField
          form={form}
          name="is_terminal"
          label="Terminal phase"
        />
      </div>
    </FormShell>
  );
}

export default PhaseForm;
